import { Link } from '@tanstack/react-router';
import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  INTERVIEW_MODES,
  INTERVIEW_MODE_LABELS,
  INTERVIEW_NOTES_MAX,
  INTERVIEW_STATUS_LABELS,
  OPEN_INTERVIEW_STATUSES,
  interviewEndsAt,
  type InterviewMode,
  type InterviewSummaryView,
  type PlannerView,
} from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { Drawer } from '../../../components/Drawer';
import { EditableTable, type CellChange, type CustomEditorProps, type EditableColumn } from '../../../components/editable-table/EditableTable';
import { Input } from '../../../components/Input';
import { Popover, usePopover } from '../../../components/Popover';
import { Select } from '../../../components/Select';
import { SkeletonLines } from '../../../components/Skeleton';
import { INTERVIEW_STATUS_TONES } from '../../../components/status-tones';
import { formatDateTime } from '../../../lib/format';
import { useInterview } from '../../interviews/api/interviews-api';
import { placeLine } from '../../interviews/interview-ui';
import { useAddPlannerRow, usePlannerSave, type PlannerEdit } from '../api/contact-api';
import { PanelPills } from './PanelPills';
import styles from './contact.module.css';

// R7 — PLN-01, the interview planner (F24.08, PRD §4.6 PL-1…PL-8): S19's interviews as a
// Notion-style table. Each cell saves on its own and flashes; a double booking warns in the
// cell before saving; "+ Add row" moves a Confirmed person to Interview 1 (DECISIONS-LOG
// "Start R7" Q1). A row's ⋯ → Open shows its history (PL-7).

interface PlannerTableProps {
  readonly jobId: string;
  readonly view: PlannerView;
  readonly zone: string;
  readonly openId: string | null;
  readonly onOpen: (interviewId: string | null) => void;
}

const isOpenInterview = (row: InterviewSummaryView): boolean => OPEN_INTERVIEW_STATUSES.includes(row.status);

export function PlannerTable({ jobId, view, zone, openId, onOpen }: PlannerTableProps): ReactNode {
  const save = usePlannerSave(jobId);
  const people = view.people.map((person) => ({ id: person.id, fullName: person.fullName }));

  async function onCommit(change: CellChange<InterviewSummaryView>): Promise<void> {
    const original = view.rows.find((row) => row.id === change.rowKey);
    if (!original) return;
    const edit = editOf(change.columnKey, change.row);
    if (edit) await save(original, change.row, edit, change.isConfirmed);
  }

  const columns: readonly EditableColumn<InterviewSummaryView>[] = [
    { key: 'candidate', header: 'Candidate', width: '14%', cell: { kind: 'display', render: (row) => <span className={styles.strong}>{row.candidateName}</span> } },
    { key: 'round', header: 'Round', width: '10%', cell: { kind: 'display', render: (row) => row.roundName } },
    {
      key: 'panel',
      header: 'Interviewer(s)',
      width: '18%',
      isEditable: isOpenInterview,
      cell: {
        kind: 'people',
        people,
        placeholder: 'Choose who interviews',
        render: (row) => <PanelPills panel={row.panel} />,
        value: (row) => row.panel.map((member) => member.userId),
        update: (row, userIds) => ({
          ...row,
          panel: userIds.map(
            (userId) =>
              row.panel.find((member) => member.userId === userId) ?? {
                userId,
                fullName: people.find((person) => person.id === userId)?.fullName ?? '—',
                role: 'counting',
                substitutedForName: null,
                didAttend: null,
              },
          ),
        }),
      },
    },
    {
      key: 'time',
      header: 'Date & time',
      width: '18%',
      isEditable: isOpenInterview,
      cell: {
        kind: 'datetime',
        timeZone: zone,
        placeholder: 'Pick a time',
        value: (row) => row.startsAt,
        update: (row, iso) => ({ ...row, startsAt: iso, endsAt: interviewEndsAt(new Date(iso), row.durationMinutes).toISOString(), status: row.status === 'unscheduled' ? 'scheduled' : row.status }),
      },
    },
    {
      key: 'where',
      header: 'Where',
      width: '16%',
      isEditable: isOpenInterview,
      cell: {
        kind: 'custom',
        render: (row) => <span className={styles.stack}>{placeLine(row)}</span>,
        editor: (props) => <WhereEditor {...props} rooms={view.rooms} />,
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '12%',
      cell: {
        kind: 'display',
        render: (row) => (
          <Chip tone={INTERVIEW_STATUS_TONES[row.status]} plain={row.status !== 'unscheduled'}>
            {INTERVIEW_STATUS_LABELS[row.status]}
          </Chip>
        ),
      },
    },
    {
      key: 'notes',
      header: 'Notes',
      isEditable: isOpenInterview,
      cell: { kind: 'text', placeholder: 'Add a note', maxLength: INTERVIEW_NOTES_MAX, value: (row) => row.notes ?? '', update: (row, notes) => ({ ...row, notes: notes.trim() === '' ? null : notes }) },
    },
  ];

  return (
    <div className={styles.planner}>
      <EditableTable
        label="Interview planner"
        columns={columns}
        rows={view.rows}
        rowKey={(row) => row.id}
        rowLabel={(row) => `${row.candidateName}, ${row.roundName}`}
        onCommit={onCommit}
        onOpen={(id) => onOpen(id)}
        isReadOnly={!view.canEdit}
        emptyText="No interviews yet. Add someone who confirmed."
        hasSavedWord
      />
      {view.canEdit && <AddRow jobId={jobId} view={view} />}
      <InterviewPanel interviewId={openId} zone={zone} onClose={() => onOpen(null)} />
    </div>
  );
}

/** The cell's change, as the call it is. */
function editOf(columnKey: string, row: InterviewSummaryView): PlannerEdit | null {
  switch (columnKey) {
    case 'time':
      return row.startsAt ? { kind: 'time', startsAt: row.startsAt } : null;
    case 'panel':
      return { kind: 'panel', userIds: row.panel.map((member) => member.userId) };
    case 'where':
      return { kind: 'where', mode: row.mode, meetingLink: row.meetingLink, roomId: row.roomId };
    case 'notes':
      return { kind: 'notes', notes: row.notes ?? '' };
    default:
      return null;
  }
}

/** PLN-01 "Where": online with its link, in person in a room, or by phone — this interview only. */
function WhereEditor({ row, label, onDone, rooms }: CustomEditorProps<InterviewSummaryView> & { readonly rooms: PlannerView['rooms'] }): ReactNode {
  const [mode, setMode] = useState<InterviewMode>(row.mode);
  const [link, setLink] = useState(row.meetingLink ?? '');
  const [roomId, setRoomId] = useState<string | null>(row.roomId);
  const isLinkValid = link.trim() === '' || /^https:\/\/\S+$/i.test(link.trim());

  function finish(): void {
    const meetingLink = mode === 'online' && link.trim() !== '' ? link.trim() : null;
    const room = mode === 'in_person' ? roomId : null;
    const isSame = mode === row.mode && meetingLink === row.meetingLink && room === row.roomId;
    onDone(isSame ? null : { ...row, mode, meetingLink, roomId: room, roomName: rooms.find((option) => option.id === room)?.name ?? null });
  }

  return (
    <div
      className={styles.whereEditor}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && isLinkValid) {
          event.preventDefault();
          finish();
        }
      }}
    >
      <Select label={`${label}: how`} value={mode} options={INTERVIEW_MODES.map((value) => ({ value, label: INTERVIEW_MODE_LABELS[value] }))} onChange={(value) => setMode(INTERVIEW_MODES.find((option) => option === value) ?? mode)} />
      {mode === 'online' && <Input aria-label={`${label}: meeting link`} value={link} placeholder="https://…" isInvalid={!isLinkValid} onChange={(event) => setLink(event.target.value)} autoFocus />}
      {mode === 'in_person' &&
        (rooms.length > 0 ? (
          <Select label={`${label}: room`} value={roomId} placeholder="Choose a room" options={rooms.map((room) => ({ value: room.id, label: room.name }))} onChange={setRoomId} />
        ) : (
          <span className={styles.meta}>No rooms yet — HR adds them under Admin → Rooms.</span>
        ))}
      <span className={styles.inlineButtons}>
        <Button size="sm" variant="solid" onClick={finish} disabled={!isLinkValid}>
          Set
        </Button>
        <Button size="sm" variant="ghost" onClick={() => onDone(null)}>
          Cancel
        </Button>
      </span>
    </div>
  );
}

/** "+ Add row": pick a Confirmed person; they move to Interview 1 and their row appears. */
function AddRow({ jobId, view }: { readonly jobId: string; readonly view: PlannerView }): ReactNode {
  const { anchor, triggerRef, toggle, close } = usePopover();
  const { add, isAdding } = useAddPlannerRow(jobId);
  const isBlocked = view.usesAssessment || view.firstRoundName === null;
  const hint = view.usesAssessment
    ? 'People on this job take the assessment before an interview.'
    : view.firstRoundName === null
      ? 'This job has no interview round yet.'
      : view.candidates.length === 0
        ? 'Mark someone Confirmed in the tracker to plan their interview.'
        : null;

  return (
    <div className={styles.addRow}>
      <Button ref={triggerRef} variant="ghost" size="sm" icon={Plus} onClick={toggle} disabled={isBlocked || view.candidates.length === 0 || isAdding}>
        {isAdding ? 'Adding…' : 'Add row'}
      </Button>
      {hint && <span className={styles.meta}>{hint}</span>}
      <Popover anchor={anchor} onClose={close} label="Who to plan" align="left">
        <div className={styles.menu} role="menu" aria-label={`Plan ${view.firstRoundName ?? 'an interview'} for`}>
          <p className={styles.menuTitle}>{view.firstRoundName} for…</p>
          {view.candidates.map((candidate) => (
            <button
              key={candidate.applicationId}
              type="button"
              role="menuitem"
              className={styles.menuButton}
              onClick={() => {
                close();
                void add({ applicationId: candidate.applicationId, version: candidate.version, candidateName: candidate.candidateName });
              }}
            >
              {candidate.candidateName}
            </button>
          ))}
        </div>
      </Popover>
    </div>
  );
}

/** PL-7: a row's side panel — its history now; comments and marks later (the "clickable cell" idea). */
export function InterviewPanel({ interviewId, zone, onClose }: { readonly interviewId: string | null; readonly zone: string; readonly onClose: () => void }): ReactNode {
  return (
    <Drawer isOpen={interviewId !== null} onClose={onClose} title="Interview" meta="History">
      {interviewId && <InterviewHistory interviewId={interviewId} zone={zone} />}
    </Drawer>
  );
}

function InterviewHistory({ interviewId, zone }: { readonly interviewId: string; readonly zone: string }): ReactNode {
  const interview = useInterview(interviewId);
  if (interview.isPending) return <SkeletonLines count={5} />;
  if (!interview.data) return <p className={styles.meta}>This interview couldn’t be loaded.</p>;
  const row = interview.data;
  return (
    <div className={styles.panel}>
      <p className={styles.panelLead}>
        <strong>{row.candidateName}</strong> · {row.roundName}
        <br />
        {row.startsAt ? formatDateTime(row.startsAt, { timeZone: zone }) : 'No time yet'} · {placeLine(row)}
      </p>
      <Link to="/interviews/$interviewId" params={{ interviewId: row.id }} className={styles.action}>
        Open the interview
      </Link>
      <h3 className={styles.panelTitle}>History</h3>
      <ol className={styles.history}>
        {row.history.length === 0 && <li className={styles.meta}>Nothing yet.</li>}
        {row.history.map((item) => (
          <li key={`${item.at}:${item.eventType}`}>
            <span>{item.text}</span>
            <span className={styles.meta}>
              {formatDateTime(item.at, { timeZone: zone })}
              {item.actorName ? ` · ${item.actorName}` : ''}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
